<!--  Temporary Code --> 
<template>
  <HeaderBar />
  <div class="signup-page">
    <el-card class="signup-card" shadow="hover" role="region" aria-label="Signup form">
      <el-row :gutter="16">
        <el-col :xs="24" :md="13" class="card-left">
          <h1 class="card-title">Create Account</h1>
          <p class="card-subtitle">Sign up to access your dashboard</p>
          <form @submit.prevent="submitForm" class="signup-form" autocomplete="on" novalidate>
            <el-form label-position="top">
              <el-form-item>
                <el-input id="name" v-model.trim="name" type="text" placeholder="Full name" autocomplete="name" aria-label="Full name" clearable style="width: 350px; display: flex; margin: 0 auto;" >
                  <template #prefix>
                    <el-icon size="large"><User /></el-icon>
                  </template>
                </el-input>
              </el-form-item>
              <el-form-item>
                <el-input id="email" v-model.trim="email" type="email" placeholder="Email" aria-label="Email" clearable style="width: 350px; display: flex; margin: 0 auto;" >
                  <template #prefix>
                    <el-icon size="large"><Message /></el-icon>
                  </template>
                </el-input>
              </el-form-item>
              <el-form-item>
                <el-tooltip placement="left" effect="dark" raw-content :content="pwTooltip">
                  <el-input
                    id="password"
                    v-model="password"
                    type="password"
                    placeholder="Password"
                    show-password
                    style="width: 350px; display: flex; margin: 0 auto;"
                  >
                    <template #prefix>
                      <el-icon size="large"><Lock /></el-icon>
                    </template>
                  </el-input>
                </el-tooltip>
              </el-form-item>
              <el-form-item>
                <el-input
                  :type="confirmPasswordVisible ? 'text' : 'password'"
                  id="confirmPassword"
                  v-model="confirmPassword"
                  placeholder="Confirm password"
                  show-password
                  aria-label="Confirm password"
                  style="width: 350px; display: flex; margin: 0 auto;"
                >
                   <template #prefix>
                    <el-icon size="large"><Lock /></el-icon>
                  </template>
                </el-input>
              </el-form-item>
            </el-form>

            <div class="actions">
              <el-button type="primary" native-type="submit" :disabled="submitting" size="large" style="width: 350px; display: flex; margin: 0 auto;">
                <span v-if="!submitting">Sign Up</span>
                <span v-else>Creating your account…</span>
              </el-button>
            </div>
            <el-divider content-position="center" style="width: 350px; display: flex; margin: 20px auto;">Or</el-divider>
            <el-button
              type="default"
              class="google-btn"
              @click="signUpWithGoogle"
              aria-label="Sign up with Google"
              style="display: flex; margin: 0 auto;"
            >
              <img src="@/assets/google-signup-logo.png" alt="Sign up with Google" />
            </el-button>
          </form>
          <p class="signup-text" style="display: flex; margin: 10px auto;">
            Already have an account?&nbsp;
            <router-link to="/login" class="signup-link">Sign In</router-link>
          </p>
        </el-col>
        <el-col :xs="24" :md="11" class="card-right" aria-hidden="false">
          <div class="hero-slot">
            <img src="@/assets/Login-Page-Animation.jpg" alt="Illustration" />
          </div>
          <div class="company-info">
            <h3>Bridging Gaps in Research</h3>
            <p>We connect researchers and resources to fuel discovery.</p>
          </div>
        </el-col>
      </el-row>
    </el-card>
  </div>
</template>

<script>
import { auth } from '@/firebase'
import {
  createUserWithEmailAndPassword,
  updateProfile,
  deleteUser,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth'
import HeaderBar from '@/components/HeaderBar.vue'
import { ElMessage } from 'element-plus'
import { mergeUserProfile } from '@/services/userProfile'

export default {
  name: 'SignupView',
  components: { HeaderBar },
  data() {
    return {
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
      passwordVisible: false,
      confirmPasswordVisible: false,
      submitting: false,
      passwordError: '',
      confirmMismatch: false,
      error: '',
      success: '',
      pwChecks: { minLength: false, upper: false, lower: false, number: false, special: false },
      pwTooltip: '<strong>Password must include:</strong><br>• Minimum 8 characters<br>• At least one uppercase letter<br>• At least one lowercase letter<br>• At least one number<br>• At least one special character'
    }
  },
  watch: {
    password(val) { this.pwChecks = this.pwChecksObj(val || '') }
  },
  methods: {
    showMessage(type, message) {
      if (!message) return
      ElMessage({ type, message, showClose: true, duration: 4500 })
    },
    pwChecksObj(pw) {
      return {
        minLength: pw.length >= 8,
        upper: /[A-Z]/.test(pw),
        lower: /[a-z]/.test(pw),
        number: /[0-9]/.test(pw),
        special: /[!@#$%^&*()_\-]/.test(pw),
      }
    },
    validatePassword(pw) {
      const checks = this.pwChecksObj(pw)
      const missing = Object.keys(checks).filter(k => !checks[k])
      return { ok: missing.length === 0, missing, checks }
    },
    async submitForm() {
      if (this.submitting) return
      this.error = ''
      this.success = ''
      this.passwordError = ''
      this.confirmMismatch = false
      const name = this.name.trim()
      const email = this.email.trim().toLowerCase()
      const password = this.password
      const confirmPassword = this.confirmPassword
      if (!name) { this.error = 'Please enter your full name.'; this.showMessage('error', this.error); return }
      const { ok, missing, checks } = this.validatePassword(password)
      this.pwChecks = checks
      if (!ok) {
        const labelMap = {
          minLength: 'Minimum 8 characters',
          upper: 'At least one uppercase letter',
          lower: 'At least one lowercase letter',
          number: 'At least one number',
          special: 'At least one special character'
        }
        const missingLabels = missing.map(k => labelMap[k] || k)
        this.passwordError = 'Password is missing: ' + missingLabels.join(', ')
        this.showMessage('error', this.passwordError)
        return
      }
      if (password !== confirmPassword) { this.confirmMismatch = true; this.showMessage('error', 'Passwords do not match.'); return }
      this.submitting = true
      let userCred = null
      try {
        userCred = await createUserWithEmailAndPassword(auth, email, password)
        try { await updateProfile(userCred.user, { displayName: name }) } catch {}
        await mergeUserProfile(userCred.user, {
          name: name || null,
          email: email || null,
          role: 'User',
          status: 'Active',
        })
        this.success = 'Account created successfully.'
        this.showMessage('success', this.success)
        this.$router.push({ name: 'CompleteProfile' })
      } catch (err) {
        if (err?.code === 'auth/email-already-in-use') this.error = 'That email is already registered. Try signing in instead.'
        else if (err?.code === 'auth/invalid-email') this.error = 'Invalid email address.'
        else if (err?.code === 'permission-denied' || /insufficient permissions/i.test(err?.message || '')) {
          this.error = 'Account created but saving profile was blocked.'
          if (userCred?.user) { try { await deleteUser(userCred.user) } catch {} }
        } else {
          this.error = err?.message || 'Registration failed. Please try again.'
          if (userCred?.user && /auth\/|permission-denied/i.test(err?.code || '')) {
            try { await deleteUser(userCred.user) } catch {}
          }
        }
        if (this.error) this.showMessage('error', this.error)
      } finally { this.submitting = false }
    },
    async signUpWithGoogle() {
      if (this.submitting) return
      this.error = ''
      this.success = ''
      this.submitting = true
      try {
        const provider = new GoogleAuthProvider()
        const cred = await signInWithPopup(auth, provider)
        const user = cred.user
        if (!user) throw new Error('Google sign-in failed')
        const email = (user.email || '').toLowerCase()
        if (!email) throw new Error('Google account has no email')
        await mergeUserProfile(user, {
          name: user.displayName || null,
          email: email || null,
          role: 'User',
          status: 'Active',
        })
        this.success = 'Account created successfully.'
        this.showMessage('success', this.success)
        this.$router.push({ name: 'CompleteProfile' })
      } catch (err) {
        this.error = err?.code === 'auth/popup-closed-by-user' ? 'Google sign-in was cancelled.' : (err?.message || 'Google sign-up failed')
        if (this.error) this.showMessage('error', this.error)
      } finally { this.submitting = false }
    }
  }
}
</script>

<style scoped>
.signup-page {
  font-family: "Roboto", "Segoe UI", sans-serif, "Helvetica Neue", Arial, system-ui, -apple-system;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: linear-gradient(180deg, #f3f8ff 0%, #e8f0ff 100%);
  padding: 20px 1rem 40px;
  box-sizing: border-box;
}

.signup-card {
  width: min(900px, 96%);
  border-radius: 22px;
  box-shadow: 0 20px 50px rgba(12,34,60,0.12);
  background: #ffffff;
  color: #003831;
}

.card-left {
  padding: auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 14px;
  box-sizing: border-box;
  background: #ffffff;
}

.card-title {
  align-self: center;
  margin: 0;
  font-size: 2rem;
  font-weight: 700;
  color: #072a23;
}


.hero-slot img {
  max-width: 240px;
  width: 100%;
  height: auto;
  display: block;
  border-radius: 14px;
  box-shadow: 0 12px 32px rgba(0,0,0,0.18);
  background: rgba(255,255,255,0.06);
  object-fit: contain;
}

.card-subtitle {
  align-self: center;
  margin: 0 0 12px 0;
  color: #476e67;
  font-size: 16px;
}

.card-right {
  padding: 28px;
  background: linear-gradient(180deg, #003831 0%, #1a6a54 100%);
  color: #ffffff;
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:center;
  gap:18px;
  box-sizing: border-box;
  text-align: center;
}

.company-info { max-width: 320px; }
.company-info h3 { margin: 0 0 6px 0; font-size: 1.2rem; font-weight: 700; }
.company-info p { margin: 0; color: #e5f3ef; }

.form-success {
  color:#1b5e20;
  background:#e8f5e9;
  padding:8px 10px;
  border-radius:8px;
  border:1px solid rgba(27,94,32,0.12);
}
.google-btn {
  background: transparent;
  padding: 0;
  cursor: pointer;
  display: inline-block;
}

.google-btn img {
  display: flex;
  height: 42px;
  width: auto;
  max-width: 100%;
  border: 1px solid rgba(3,56,49,0.12);
  border-color: grey;
  box-shadow: 0 6px 12px rgba(3,56,49,0.06);
}

.google-btn:focus {
  outline: 3px solid rgba(11,92,255,0.16);
  border-radius: 6px;
}

.signup-text {
  margin-top:10px;
  color:#516a64;
  font-size:0.95rem;
}

.signup-link {
  color: #0b6cff;
  text-decoration: none;
  font-weight: 700;
  margin-left:6px;
}
.signup-link:hover { text-decoration: underline; }

@media (max-width: 880px) {
  .card-left, .card-right { flex: 1 1 auto; width: 100%; }
  .card-right { order: -1; }
  .hero-slot img { max-width: 180px; }
}
</style>
